import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Container, Grid } from "@mui/material";
import type ApplicationRoles from "constants/ApplicationRoles";
import type Statut from "constants/Statut";
import type Commande from "models/Commande";
import { Authorization } from "@sdeapps/react-core";
import type { User } from "@sdeapps/react-core";
import graphService from "services/graphService";
import CommandesFilter from "components/molecules/CommandesFilter";
import type Article from "models/Article";
import articlesService from "services/articlesService";
import PageTitle from "components/molecules/PageTitle";
import FilterCommandesPageSkeleton from "./FilterCommandesPageSkeleton";
import { useDispatch, useSelector } from "react-redux";
import type { StoreProps } from "store/store";
import type store from "store/store";
import { getAllCommandes } from "reducers/commandesReducer";
import type { CommandesReducerType } from "reducers/commandesReducer";
import LazyLoadedCommandeList from "./LazyLoadedCommandeListe";

type FilterCommandesPageProps = {
  availableStatuts: Array<Statut>;
  defaultStatuts: Array<Statut>;
  roles: ApplicationRoles | Array<ApplicationRoles>;
  pageTitle?: string;
  getCommandeUrl?: (id: string) => string;
  hasCommandeLink?: boolean;
};

function FilterCommandesPage({
  availableStatuts,
  defaultStatuts,
  roles,
  pageTitle = "",
  getCommandeUrl,
  hasCommandeLink = false,
}: Readonly<FilterCommandesPageProps>): ReactNode {
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [filteredCommandes, setFilteredCommandes] = useState<Array<Commande>>([]);
  const [agents, setAgents] = useState<Array<User>>([]);
  const [articles, setArticles] = useState<Array<Article>>([]);

  const dispatch = useDispatch<typeof store.dispatch>();
  const commandes = useSelector<StoreProps, CommandesReducerType>((state) => state?.commandes);

  useEffect(() => {
    void dispatch(getAllCommandes());
  }, [dispatch]);

  useEffect(() => {
    async function getAgentsAndArticles(): Promise<void> {
      const [_agents, _articles] = await Promise.all([
        graphService.getAgentsByIds(commandes.list.map((c) => c.demandeurId)),
        articlesService.getAll(),
      ]);
      setAgents(_agents.toSorted((a: User, b: User) => (a.displayName > b.displayName ? 1 : -1)));
      setArticles(_articles.toSorted((a, b) => (a.label > b.label ? 1 : -1)));
    }
    if (!commandes.isLoading) {
      void getAgentsAndArticles();
    }
  }, [commandes.isLoading, commandes.list]);

  useEffect(() => {
    if (!commandes.isLoading && isInitialLoading) {
      setIsInitialLoading(false);
    }
  }, [commandes.isLoading, isInitialLoading]);

  // On n'affiche le skeleton que lors du premier chargement, sinon cela réinitialise le filtre à chaque changement
  if (isInitialLoading) {
    return <FilterCommandesPageSkeleton />;
  }

  return (
    <Authorization roles={roles}>
      <PageTitle title={pageTitle} />
      <Container maxWidth="xl">
        <Grid container spacing={2} size={12} sx={{ alignItems: "center" }}>
          <CommandesFilter
            commandes={commandes.list}
            agentFilter
            articleFilter
            centreRetraitFilter
            statutFilter
            numeroBonDeCommandeFilter
            selectedStatutsDefault={defaultStatuts}
            availableAgents={agents}
            setFilteredCommandes={setFilteredCommandes}
            availableStatuts={availableStatuts}
            availableArticles={articles}
          />
          <LazyLoadedCommandeList
            commandes={filteredCommandes}
            agents={agents}
            hasCommandeLink={hasCommandeLink}
            uri={getCommandeUrl}
            displayCommentaires
          />
        </Grid>
      </Container>
    </Authorization>
  );
}

export default FilterCommandesPage;
